import React from 'react';
import { Trash2, Key, Plus, ToggleLeft, ToggleRight, Music, Headphones, Link2, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Admin dashboard "invites" tab (extracted from AdminPage).
export const AdminInvitesTab = ({
  codeLoading,
  copyInviteLink,
  createInviteCode,
  deleteInviteCode,
  formatDateOnly,
  inviteCodes,
  newCode,
  newMaxUses,
  newRoleType,
  setNewCode,
  setNewMaxUses,
  setNewRoleType,
  toggleCodeActive,
}) => (
  <div className="space-y-6">
    {/* Create New Code */}
    <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 p-5">
      <h3 className="font-semibold mb-4 flex items-center gap-2">
        <Plus className="w-4 h-4" />
        Create Invite Code
      </h3>
      <div className="flex gap-3 flex-wrap items-end">
        <Input
          type="text"
          placeholder="CODE NAME"
          value={newCode}
          onChange={(e) => setNewCode(e.target.value.toUpperCase())}
          className="bg-zinc-900 border-zinc-700 text-white uppercase w-40"
          maxLength={20}
          data-testid="new-code-input"
        />
        <div className="flex items-center gap-2">
          <span className="text-zinc-400 text-sm">Max uses:</span>
          <Input
            type="number"
            value={newMaxUses}
            onChange={(e) => setNewMaxUses(parseInt(e.target.value) || 1)}
            min={1}
            className="bg-zinc-900 border-zinc-700 text-white w-20"
            data-testid="max-uses-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-zinc-400 text-sm">Role:</span>
          <Select value={newRoleType} onValueChange={setNewRoleType}>
            <SelectTrigger className="w-32 bg-zinc-900 border-zinc-700 text-white" data-testid="role-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="listener" className="text-white">
                <div className="flex items-center gap-2">
                  <Headphones className="w-4 h-4 text-blue-400" />
                  Listener
                </div>
              </SelectItem>
              <SelectItem value="artist" className="text-white">
                <div className="flex items-center gap-2">
                  <Music className="w-4 h-4 text-orange-400" />
                  Artist
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={createInviteCode}
          disabled={codeLoading || !newCode.trim()}
          className="bg-orange-500 hover:bg-orange-600 text-white"
          data-testid="create-code-btn"
        >
          {codeLoading ? 'Creating...' : 'Create Code'}
        </Button>
      </div>
    </div>

    {/* Invite Codes List */}
    <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden">
      <div className="px-5 py-4 border-b border-zinc-800">
        <h3 className="font-semibold">All Invite Codes ({inviteCodes.length})</h3>
      </div>

      {inviteCodes.length === 0 ? (
        <div className="px-5 py-8 text-center text-zinc-500">
          <Key className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No invite codes yet. Create one above.</p>
        </div>
      ) : (
        <div className="divide-y divide-zinc-800">
          {inviteCodes.map((code) => (
            <div key={code.id} className="px-5 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-2 h-2 rounded-full ${code.active ? 'bg-green-500' : 'bg-red-500'}`} />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-mono text-white font-semibold tracking-wider">{code.code}</p>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        code.role_type === 'artist' 
                          ? 'bg-orange-500/20 text-orange-400' 
                          : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {code.role_type === 'artist' ? 'Artist' : 'Listener'}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-500">
                      {code.uses} / {code.max_uses} uses • Created {formatDateOnly(code.created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    code.uses >= code.max_uses 
                      ? 'bg-zinc-700 text-zinc-400' 
                      : code.active 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-red-500/20 text-red-400'
                  }`}>
                    {code.uses >= code.max_uses ? 'Exhausted' : code.active ? 'Active' : 'Disabled'}
                  </span>
                  <button
                    onClick={() => copyInviteLink(code.code)}
                    className="p-2 hover:bg-blue-500/20 rounded-lg transition-colors text-blue-400"
                    title="Copy Invite Link"
                  >
                    <Link2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => toggleCodeActive(code.id, code.active)}
                    className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                    title={code.active ? 'Disable' : 'Enable'}
                  >
                    {code.active ? (
                      <ToggleRight className="w-5 h-5 text-green-500" />
                    ) : (
                      <ToggleLeft className="w-5 h-5 text-zinc-500" />
                    )}
                  </button>
                  <button
                    onClick={() => deleteInviteCode(code.id)}
                    className="p-2 hover:bg-red-500/20 rounded-lg transition-colors text-red-400"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {/* Shareable Link Preview */}
              <div className="mt-2 ml-6 flex items-center gap-2">
                <span className="text-xs text-zinc-600">Link:</span>
                <code className="text-xs text-zinc-500 bg-zinc-800/50 px-2 py-1 rounded font-mono">
                  {window.location.origin}/j/{code.code}
                </code>
                <button
                  onClick={() => copyInviteLink(code.code)}
                  className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                >
                  <Copy className="w-3 h-3" />
                  Copy
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);
